import { UserType, getCmsRequest, getPermissionUtils } from '@tyro/api';
import { getNumber, lazyWithRetry, throw404Error } from '@tyro/core';
import { PieChartIcon } from '@tyro/icons';
import { getNoticeBoard } from '@tyro/notice-board';
import { type NavObjectFunction, NavObjectType } from '@tyro/router/types';
import type { WhatsNewDetails } from './@types/whats-new';
import { getWhatsNewItemDetailsPath } from './utils/whats-new-api-paths';

const Dashboard = lazyWithRetry(() => import('./pages/dashboard'));
const ViewWhatsNewItem = lazyWithRetry(() => import('./pages/whats-new/view'));

export const getShellRoutes: NavObjectFunction = (t) => [
  {
    type: NavObjectType.Category,
    title: t('navigation:general.title'),
    children: [
      {
        type: NavObjectType.RootLink,
        title: t('navigation:general.dashboard'),
        path: '/dashboard',
        hasAccess: ({ permissions }) => permissions.isStaffUser,
        icon: <PieChartIcon />,
        element: <Dashboard />,
        loader: async () => {
          return getNoticeBoard({
            active: true,
          });
        },
      },
      {
        type: NavObjectType.NonMenuLink,
        path: '/whats-new/view/:id',
        element: <ViewWhatsNewItem />,
        loader: async ({ params }) => {
          const id = getNumber(params.id);

          if (!id) throw404Error();

          const [{ data }, { userType, hasAtLeastOnePermission }] =
            await Promise.all([
              getCmsRequest<{ data: WhatsNewDetails }>(
                getWhatsNewItemDetailsPath(id),
              ),
              getPermissionUtils(),
            ]);

          if (userType === UserType.Tyro) {
            return data;
          }

          const { filter_usertype, filter_permissions } = data ?? {};

          const isForUserType = filter_usertype?.includes(userType!);
          const hasPermissions =
            !filter_permissions ||
            filter_permissions.length === 0 ||
            hasAtLeastOnePermission(filter_permissions);

          if (!isForUserType || !hasPermissions) {
            throw new Response('Forbidden', { status: 403 });
          }

          return data;
        },
      },
    ],
  },
];
